/*
 *  Copyright Amazon.com, Inc. or its affiliates. All Rights Reserved.
 *
 *  Licensed under the Apache License, Version 2.0 (the "License").
 *  You may not use this file except in compliance with the License.
 *  A copy of the License is located at
 *
 *  http://aws.amazon.com/apache2.0
 *
 *  or in the "license" file accompanying this file. This file is distributed
 *  on an "AS IS" BASIS, WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either
 *  express or implied. See the License for the specific language governing
 *  permissions and limitations under the License.
 */

import React from 'react';
import { observer } from 'mobx-react';
import { action, decorate, observable, runInAction } from 'mobx';
import { Icon } from 'semantic-ui-react';

import FileUpload from '../files/FileUpload';
import { getPresignedStudyUploadRequests } from '../../helpers/api';
import upload from '../../helpers/xhr-upload';

const iconStyle = { lineHeight: 1, height: '1em', margin: 0 };

// expected props
// - studyId
class UploadStudyFiles extends React.Component {
  constructor(props) {
    super(props);
    runInAction(() => {
      this.modalOpen = false;
    });
    this.fileUploadHandler = this.fileUploadHandler.bind(this);
  }

  async fileUploadHandler(fileUpload) {
    const file = fileUpload.getFile();
    if (!file) {
      throw new Error('No file');
    }

    let uploadRequest;
    try {
      const presignResult = await getPresignedStudyUploadRequests(this.props.studyId, fileUpload.fullFilePath);
      uploadRequest = presignResult[fileUpload.fullFilePath];
    } catch (error) {
      const errMessage = 'Error occurred obtaining presigned request';
      console.error(`${errMessage}:`, error);
      throw new Error(errMessage);
    }

    if (!uploadRequest) {
      throw new Error('Failed to obtain presigned request');
    }

    const uploadHandle = upload(file, uploadRequest.url, uploadRequest.fields);
    fileUpload.setCancel(uploadHandle.cancel);
    uploadHandle.onProgress(fileUpload.updateProgress);

    try {
      await uploadHandle.done;
    } catch (error) {
      const errMessage = 'Error encountered while uploading file';
      console.error(`${errMessage}:`, error);
      throw new Error(errMessage);
    }
  }

  handleClose = action(() => { this.modalOpen = false; });

  handleOpen = action(() => { this.modalOpen = true; });

  handleOverlayClick = (e) => {
    if (e.target === e.currentTarget) this.handleClose();
  };

  render() {
    return (
      <>
        {this.renderTrigger()}
        {this.modalOpen && this.renderModal()}
      </>
    );
  }

  renderModal() {
    return (
      <div className="rmit-modal-overlay" onClick={this.handleOverlayClick} role="presentation">
        <div className="rmit-modal upload-modal">
          <div className="upload-modal-header">
            <div className="upload-modal-icon">
              <Icon name="upload" style={{ ...iconStyle, fontSize: '1.4em' }} />
            </div>
            <h3 className="upload-modal-title">Upload Study Files</h3>
            <button
              onClick={this.handleClose}
              aria-label="Close upload dialog"
              className="upload-modal-close"
            >
              <Icon name="close" style={iconStyle} />
            </button>
          </div>
          <div className="upload-modal-body">
            <FileUpload resourceId={this.props.studyId} fileUploadHandler={this.fileUploadHandler} />
          </div>
        </div>
      </div>
    );
  }

  renderTrigger() {
    return (
      <button
        className="workspace-action-btn workspace-action-btn-secondary"
        onClick={this.handleOpen}
      >
        <Icon name="upload" style={iconStyle} /> Upload
      </button>
    );
  }
}

decorate(UploadStudyFiles, {
  modalOpen: observable,
});

export default observer(UploadStudyFiles);
